import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  Button,
  LinearProgress,
  Avatar,
  IconButton,
  alpha,
} from "@mui/material";
import { 
  School as SchoolIcon,
  PlayArrow as DemoIcon,
  CheckCircle as BilledIcon,
  Cancel as NotBilledIcon,
  Warning as ExpiredIcon,
  AttachMoney as RevenueIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as TrainingIcon,
  ArrowForward as ArrowForwardIcon,
  Help as QueryIcon,
  CalendarToday as ScheduleIcon,
  Payment as PaymentIcon,
  Dashboard as DashboardIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import StatCard from "../components/infographics/StatCard";

const AdminDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Mock data - Dashboard uses static data
  const stats = {
    totalSchools: 247,
    demoSchools: 23,
    billedSchools: 189,
    notBilledSchools: 35,
    expiredSchools: 12,
    totalRevenue: 2450000,
    monthlyRevenue: 185000,
    activeTraining: 45,
    conversionRate: 68.5,
    pendingQueries: 12,
    schedules: 8,
  };

  const recentSchools = [
    { id: 1, name: "Greenwood High School", status: "Billed", revenue: 45000, joinDate: "2024-06-15" },
    { id: 2, name: "Sunshine Academy", status: "Demo", revenue: 0, joinDate: "2024-06-20" },
    { id: 3, name: "Riverside School", status: "Billed", revenue: 38000, joinDate: "2024-06-18" },
    { id: 4, name: "Mountain View School", status: "Not Billed", revenue: 0, joinDate: "2024-06-22" },
  ];

  const upcomingRenewals = [
    { id: 1, name: "City Central School", daysLeft: 5, amount: 42000 },
    { id: 2, name: "Oakwood Academy", daysLeft: 12, amount: 38000 },
    { id: 3, name: "Bright Future School", daysLeft: 18, amount: 45000 },
  ];

  const pendingQueries = [
    { id: 1, schoolName: "Greenwood High School", query: "Need help with student enrollment", date: "2024-06-20", priority: "High" },
    { id: 2, schoolName: "Sunshine Academy", query: "Question about billing cycle", date: "2024-06-19", priority: "Medium" },
    { id: 3, schoolName: "Riverside School", query: "Technical support required", date: "2024-06-18", priority: "High" },
    { id: 4, schoolName: "Mountain View School", query: "Feature request for reports", date: "2024-06-17", priority: "Low" },
  ];

  const schedules = [
    { id: 1, title: "Training Session - Demo Schools", date: "2024-06-25", time: "10:00 AM", type: "Training" },
    { id: 2, title: "Monthly Review Meeting", date: "2024-06-28", time: "2:00 PM", type: "Meeting" },
    { id: 3, title: "Product Demo - New Features", date: "2024-07-02", time: "11:00 AM", type: "Demo" },
    { id: 4, title: "Follow-up Call - Billed Schools", date: "2024-07-05", time: "3:00 PM", type: "Call" },
  ];

  const pendingInstallments = [
    { id: 1, schoolName: "Greenwood High School", amount: 45000, dueDate: "2024-06-25", installmentNo: "1/3", status: "Overdue" },
    { id: 2, schoolName: "Sunshine Academy", amount: 38000, dueDate: "2024-06-28", installmentNo: "2/4", status: "Due Soon" },
    { id: 3, schoolName: "Riverside School", amount: 42000, dueDate: "2024-07-05", installmentNo: "1/3", status: "Pending" },
    { id: 4, schoolName: "Mountain View School", amount: 35000, dueDate: "2024-07-10", installmentNo: "3/5", status: "Pending" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Billed":
        return "success";
      case "Demo":
        return "info";
      case "Not Billed":
        return "warning";
      case "Expired":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Main Card Container */}
        <Card>
          {/* Header Section */}
          <Box
            sx={{
              p: 3,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <DashboardIcon sx={{ color: theme.palette.primary.main, fontSize: "1.75rem" }} />
                CRM Dashboard
              </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                Manage your school clients and grow your business
              </Typography>
            </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<SchoolIcon />}
              onClick={() => navigate("/schools")}
            >
              Add New School
            </Button>
          </Box>
          </Box>

          {/* Content Section */}
          <CardContent sx={{ p: 3 }}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >

        {/* Key Statistics */}
        <motion.div variants={itemVariants}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Schools"
                value={stats.totalSchools}
                icon={<SchoolIcon />}
                color="primary"
                trend="+12 this month"
                trendColor="success"
                onClick={() => navigate("/schools")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Demo Schools"
                value={stats.demoSchools}
                icon={<DemoIcon />}
                color="info"
                trend="+5 this week"
                trendColor="info"
                onClick={() => navigate("/schools/demo")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Billed Schools"
                value={stats.billedSchools}
                icon={<BilledIcon />}
                color="success"
                trend="+8 this month"
                trendColor="success"
                onClick={() => navigate("/schools/billed")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Billed Schools(App Only)"
                value={stats.totalRevenue}
                icon={<RevenueIcon />}
                color="success"
                trend="+15% this quarter"
                trendColor="success"
                prefix="₹"
                onClick={() => navigate("/analytics")}
              />
            </Grid>
          </Grid>
        </motion.div>

        {/* Secondary Statistics */}
        <motion.div variants={itemVariants}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Not Billed"
                value={stats.notBilledSchools}
                icon={<NotBilledIcon />}
                color="warning"
                trend="Action needed"
                trendColor="warning"
                onClick={() => navigate("/schools/not-billed")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Expired"
                value={stats.expiredSchools}
                icon={<ExpiredIcon />}
                color="error"
                trend="Renewal needed"
                trendColor="error"
                onClick={() => navigate("/schools/expired")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Active Training"
                value={stats.activeTraining}
                icon={<TrainingIcon />}
                color="secondary"
                trend="In progress"
                trendColor="info"
                onClick={() => navigate("/training")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Conversion Rate"
                value={stats.conversionRate}
                icon={<TrendingUpIcon />}
                color="success"
                trend="+2.5% this month"
                trendColor="success"
                suffix="%"
                decimals={1}
              />
            </Grid>
          </Grid>
        </motion.div>

        {/* Pending Query & Schedules */}
        <motion.div variants={itemVariants}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Pending Queries */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem", display: "flex", alignItems: "center", gap: 1 }}>
                      <QueryIcon sx={{ fontSize: "1.2rem" }} />
                      Pending Query
                    </Typography>
                    <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate("/school-query")}>
                      View All
                    </Button>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {pendingQueries.map((query) => (
                      <motion.div
                        key={query.id}
                        whileHover={{ scale: 1.01 }}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/school-query`)}
                      >
                        <Card
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            "&:hover": {
                              borderColor: theme.palette.warning.main,
                              boxShadow: 2,
                            },
                          }}
                        >
                          <CardContent sx={{ py: 1.5, px: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                                {query.schoolName}
                              </Typography>
                              <Chip
                                label={query.priority}
                                size="small"
                                color={query.priority === "High" ? "error" : query.priority === "Medium" ? "warning" : "default"}
                                sx={{ fontSize: "0.65rem", height: "20px" }}
                              />
                            </Box>
                            <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary", mb: 0.5 }}>
                              {query.query}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                              {new Date(query.date).toLocaleDateString()}
                            </Typography>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Schedules */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem", display: "flex", alignItems: "center", gap: 1 }}>
                      <ScheduleIcon sx={{ fontSize: "1.2rem" }} />
                      Schedules
                    </Typography>
                    <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate("/training")}>
                      View All
                    </Button>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {schedules.map((schedule) => (
                      <motion.div
                        key={schedule.id}
                        whileHover={{ scale: 1.01 }}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/training`)}
                      >
                        <Card
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            "&:hover": {
                              borderColor: theme.palette.info.main,
                              boxShadow: 2,
                            },
                          }}
                        >
                          <CardContent sx={{ py: 1.5, px: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                                {schedule.title}
                              </Typography>
                              <Chip
                                label={schedule.type}
                                size="small"
                                color="info"
                                sx={{ fontSize: "0.65rem", height: "20px" }}
                              />
                            </Box>
                            <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                              <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <ScheduleIcon sx={{ fontSize: "0.75rem" }} />
                                {new Date(schedule.date).toLocaleDateString()}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                                {schedule.time}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </motion.div>

        {/* Pending Installments */}
        <motion.div variants={itemVariants}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem", display: "flex", alignItems: "center", gap: 1 }}>
                      <PaymentIcon sx={{ fontSize: "1.2rem" }} />
                      Pending Installments
                    </Typography>
                    <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate("/contracts")}>
                      View All
                    </Button>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {pendingInstallments.map((installment) => (
                      <motion.div
                        key={installment.id}
                        whileHover={{ scale: 1.01 }}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/contracts`)}
                      >
                        <Card
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            "&:hover": {
                              borderColor: theme.palette.error.main,
                              boxShadow: 2,
                            },
                          }}
                        >
                          <CardContent sx={{ py: 1.5, px: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.875rem", mb: 0.25 }}>
                                  {installment.schoolName}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                                  Installment {installment.installmentNo}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: "right", mr: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem", color: theme.palette.error.main, mb: 0.25 }}>
                                  ₹{installment.amount.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                                  Due: {new Date(installment.dueDate).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Chip
                                label={installment.status}
                                size="small"
                                color={installment.status === "Overdue" ? "error" : installment.status === "Due Soon" ? "warning" : "default"}
                                sx={{ fontSize: "0.65rem", height: "20px" }}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </motion.div>

        {/* Recent Schools & Upcoming Renewals */}
        <motion.div variants={itemVariants}>
          <Grid container spacing={2}>
            {/* Recent Schools */}
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                      Recent Schools
                    </Typography>
                    <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate("/schools")}>
                      View All
                    </Button>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {recentSchools.map((school) => (
                      <motion.div
                        key={school.id}
                        whileHover={{ scale: 1.01 }}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/schools/${school.id}`)}
                      >
                        <Card
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            "&:hover": {
                              borderColor: theme.palette.primary.main,
                              boxShadow: 2,
                            },
                          }}
                        >
                          <CardContent sx={{ py: 1.5, px: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25, fontSize: "0.875rem" }}>
                                  {school.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                                  Joined: {new Date(school.joinDate).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: "right" }}>
                                <Chip
                                  label={school.status}
                                  color={getStatusColor(school.status)}
                                  size="small"
                                  sx={{ mb: 1 }}
                                />
                                {school.revenue > 0 && (
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: "success.main" }}>
                                    ₹{school.revenue.toLocaleString()}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Upcoming Renewals */}
            <Grid item xs={12} md={5}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: "1rem" }}>
                    Upcoming Renewals
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {upcomingRenewals.map((school) => (
                      <motion.div
                        key={school.id}
                        whileHover={{ scale: 1.02 }}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/schools/${school.id}`)}
                      >
                        <Card
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            "&:hover": {
                              borderColor: theme.palette.warning.main,
                              boxShadow: 2,
                            },
                          }}
                        >
                          <CardContent sx={{ py: 1.5, px: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                                {school.name}
                              </Typography>
                              <Chip
                                label={`${school.daysLeft} days`}
                                color={school.daysLeft <= 7 ? "error" : "warning"}
                                size="small"
                                sx={{ height: "20px", fontSize: "0.7rem" }}
                              />
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                                Renewal Amount
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "success.main", fontSize: "0.875rem" }}>
                                ₹{school.amount.toLocaleString()}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={(30 - school.daysLeft) / 30 * 100}
                              sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                              color={school.daysLeft <= 7 ? "error" : "warning"}
                            />
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </Box>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ mt: 2 }}
                    onClick={() => navigate("/schools/renewals")}
                  >
                    View All Renewals
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </motion.div>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default AdminDashboard;
